import type { FormikErrors, FormikHelpers } from "formik";
import { type ReactNode, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { updateClient } from "@/features/clients/api";
import type {
  AddressType,
  Client,
  ContactMethod,
  UpdateClientPayload,
} from "@/features/clients/types";
import { applyApiErrors } from "@/lib/form-errors";
import {
  DATE_RE,
  formatPhone,
  formatZip,
  maskedDateToISO,
  onlyDigits,
  PHONE_RE,
  ZIP_RE,
} from "@/lib/masks";

/** Uppercase micro-label used across every data-entry grid in the profile forms. */
export const FIELD_LABEL = "text-muted-foreground text-xs font-semibold tracking-wider uppercase";

export const CONTACT_METHODS: { value: Exclude<ContactMethod, "">; label: string }[] = [
  { value: "EMAIL", label: "Email" },
  { value: "HOME_PHONE", label: "Home phone" },
  { value: "MOBILE_PHONE", label: "Mobile phone" },
  { value: "WORK_PHONE", label: "Work phone" },
  { value: "MAIL", label: "Mail" },
];

// --- Yup helpers shared by both profile schemas -----------------------------

/** Optional masked-format field: only enforced when the user typed something. */
export function optionalMatch(re: RegExp, message: string) {
  return Yup.string().matches(re, { message, excludeEmptyString: true });
}

export const dateSchema = Yup.string()
  .matches(DATE_RE, { message: "Use MM/DD/YYYY", excludeEmptyString: true })
  .test("real-date", "Enter a valid calendar date", (v) => !v || maskedDateToISO(v) !== "");

export const phoneRule = (message = "Enter a valid phone number") =>
  optionalMatch(PHONE_RE, message);
export const zipRule = () => optionalMatch(ZIP_RE, "Enter a valid ZIP code");

/** `MM/DD/YYYY` → ISO for the API, or `null` to clear when the field is empty. */
export function dateToPayload(masked: string): string | null {
  return masked ? maskedDateToISO(masked) : null;
}

// --- Shared value shape: contact + residential/mailing address --------------
// Both the individual and business profile forms carry these fields, so their
// initial-values and payload slices are built here once.

export interface ContactAddressValues {
  email: string;
  /** Free-text profile note (legacy "explanations"); shared by both types. */
  explanation: string;
  homePhone: string;
  mobilePhone: string;
  faxNumber: string;
  workPhone: string;
  workMobile: string;
  preferredContactMethod: ContactMethod;
  homeAddressType: AddressType;
  homeStreet: string;
  homeLine2: string;
  homeCity: string;
  homeState: string;
  homeZip: string;
  homeCounty: string;
  homeTimezone: string;
  homeInternationalAddress: string;
  homeCountry: string;
  mailingIsDifferent: boolean;
  mailingAddressType: AddressType;
  mailingStreet: string;
  mailingLine2: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  mailingCounty: string;
  mailingTimezone: string;
  mailingInternationalAddress: string;
  mailingCountry: string;
}

/** Loaded client → the shared contact/address slice of a form's initial values. */
export function contactAddressInitialValues(client: Client): ContactAddressValues {
  return {
    email: client.email ?? "",
    explanation: client.explanation ?? "",
    homePhone: formatPhone(client.homePhone ?? ""),
    mobilePhone: formatPhone(client.mobilePhone ?? ""),
    faxNumber: formatPhone(client.faxNumber ?? ""),
    workPhone: formatPhone(client.workPhone ?? ""),
    workMobile: formatPhone(client.workMobile ?? ""),
    preferredContactMethod: client.preferredContactMethod ?? "",
    homeAddressType: client.homeAddressType ?? "DOMESTIC",
    homeStreet: client.homeStreet ?? "",
    homeLine2: client.homeLine2 ?? "",
    homeCity: client.homeCity ?? "",
    homeState: client.homeState ?? "",
    homeZip: formatZip(client.homeZip ?? ""),
    homeCounty: client.homeCounty ?? "",
    homeTimezone: client.homeTimezone ?? "",
    homeInternationalAddress: client.homeInternationalAddress ?? "",
    homeCountry: client.homeCountry ?? "",
    mailingIsDifferent: client.mailingIsDifferent ?? false,
    mailingAddressType: client.mailingAddressType ?? "DOMESTIC",
    mailingStreet: client.mailingStreet ?? "",
    mailingLine2: client.mailingLine2 ?? "",
    mailingCity: client.mailingCity ?? "",
    mailingState: client.mailingState ?? "",
    mailingZip: formatZip(client.mailingZip ?? ""),
    mailingCounty: client.mailingCounty ?? "",
    mailingTimezone: client.mailingTimezone ?? "",
    mailingInternationalAddress: client.mailingInternationalAddress ?? "",
    mailingCountry: client.mailingCountry ?? "",
  };
}

/** Form values → the shared contact/address slice of a PATCH body. */
export function contactAddressPayload(v: ContactAddressValues): UpdateClientPayload {
  return {
    email: v.email,
    explanation: v.explanation,
    home_phone: onlyDigits(v.homePhone),
    mobile_phone: onlyDigits(v.mobilePhone),
    fax_number: onlyDigits(v.faxNumber),
    work_phone: onlyDigits(v.workPhone),
    work_mobile: onlyDigits(v.workMobile),
    preferred_contact_method: v.preferredContactMethod,
    home_address_type: v.homeAddressType,
    home_street: v.homeStreet,
    home_line2: v.homeLine2,
    home_city: v.homeCity,
    home_state: v.homeState,
    home_zip: onlyDigits(v.homeZip),
    home_county: v.homeCounty,
    home_timezone: v.homeTimezone,
    home_international_address: v.homeInternationalAddress,
    home_country: v.homeCountry,
    mailing_is_different: v.mailingIsDifferent,
    mailing_address_type: v.mailingAddressType,
    mailing_street: v.mailingStreet,
    mailing_line2: v.mailingLine2,
    mailing_city: v.mailingCity,
    mailing_state: v.mailingState,
    mailing_zip: onlyDigits(v.mailingZip),
    mailing_county: v.mailingCounty,
    mailing_timezone: v.mailingTimezone,
    mailing_international_address: v.mailingInternationalAddress,
    mailing_country: v.mailingCountry,
  };
}

/** Yup rules for the shared contact/address fields — spread into each schema. */
export const contactAddressRules = {
  email: Yup.string().email("Enter a valid email"),
  homePhone: phoneRule(),
  mobilePhone: phoneRule(),
  faxNumber: phoneRule("Enter a valid fax number"),
  workPhone: phoneRule(),
  workMobile: phoneRule(),
  homeZip: zipRule(),
  mailingZip: zipRule(),
};

// --- Submit hook (shared save flow) -----------------------------------------

export interface ProfileFormProps {
  client: Client;
  /** Push the saved client back up so the layout header reflects any changes. */
  onClientUpdated: (client: Client) => void;
  /** Independent sections (assignments, links, dependents) rendered inside the
   * form's content column so the sticky save bar stays last. */
  extraSections?: ReactNode;
}

/**
 * Owns the shared save flow: PATCH the client, toast, bind any server field
 * errors to their inputs, and (for "Save & Continue") advance to the overview.
 * `continueRef` is wired to the two submit buttons by `ProfileFormShell`.
 */
export function useProfileSubmit<V>(opts: {
  client: Client;
  onClientUpdated: (client: Client) => void;
  toPayload: (values: V, client: Client) => UpdateClientPayload;
  toInitialValues: (client: Client) => V;
  /** Server error keys whose form field name differs (e.g. `{ ssn_tin: "taxId" }`). */
  fieldMap?: Record<string, string>;
}) {
  const navigate = useNavigate();
  const continueRef = useRef(false);

  async function handleSubmit(values: V, bag: FormikHelpers<V>) {
    // Never console.log values — they hold SSN/EIN, DOB, and other PII.
    const advance = continueRef.current;
    continueRef.current = false;
    try {
      const updated = await updateClient(opts.client.id, opts.toPayload(values, opts.client));
      opts.onClientUpdated(updated);
      bag.resetForm({ values: opts.toInitialValues(updated) });
      toast.success("Profile saved");
      if (advance) navigate(`/client/${opts.client.id}/overview`);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(
        applyApiErrors(
          err,
          { setErrors },
          { fieldMap: opts.fieldMap, fallback: "Couldn't save profile." },
        ),
      );
    }
  }

  return { handleSubmit, continueRef };
}
